import React, { createRef, useEffect, useImperativeHandle } from 'react'

import { Card, type CardData, type CardLabels, type CardProps } from '@/components/molecules/Card/Card.tsx'
import { ConfirmDialog, type ConfirmDialogLabels } from '@/components/molecules/ConfirmDialog/ConfirmDialog.tsx'

import {
  ConfirmDonationDialog,
  type ConfirmDonationDialogLabels,
  type ConfirmDonationDialogProps,
} from '../ConfirmDonationDialog/ConfirmDonationDialog.tsx'

export interface CardCompletedLabels extends CardLabels {
  changeDonationConfirmDialog: ConfirmDonationDialogLabels
  reactivateConfirmDialog: ConfirmDialogLabels
}

export interface CardCompletedState extends Pick<CardProps, 'size'> {
  state: 'default' | 'active'
  highlighted?: boolean
}

export interface CardCompletedData extends Omit<CardData, 'requestStatus'> {
  request: CardProps['request']
  requestStatus: 'unfindabled' | 'cancelled' | 'completed'
  pendingEvent?: 'changeDonation' | 'reactivateRequest'
}

export interface CardCompletedAction {
  onChangeDonation: ConfirmDonationDialogProps['onConfirm']
  onReactivate: () => void
}

export interface CardCompletedProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, CardCompletedState, CardCompletedData, CardCompletedAction {
  labels: CardCompletedLabels
}

export const CardCompleted = React.forwardRef<HTMLDivElement, CardCompletedProps>(
  (
    {
      state,
      size,
      request,
      requestStatus,
      status,
      donation,
      locationLabel,
      pendingEvent,
      labels,
      highlighted,
      onChangeDonation,
      onReactivate,
      ...props
    },
    ref
  ) => {
    const cellRef = createRef<HTMLDivElement>()
    useImperativeHandle(ref, () => cellRef.current!, [cellRef])
    useEffect(() => {
      if (highlighted) {
        cellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, [state, cellRef, highlighted])
    return (
      <Card
        className={highlighted ? 'animate-pulse-gray' : ''}
        state={state}
        size={size}
        labels={labels}
        request={request}
        requestStatus={requestStatus}
        status={status}
        donation={donation}
        locationLabel={locationLabel}
        ref={cellRef}
        {...props}>
        {state === 'active' && requestStatus === 'completed' && donation?.state !== 'digital' && (
          <div className="flex size-full justify-end">
            <ConfirmDonationDialog
              labels={labels.changeDonationConfirmDialog}
              onConfirm={onChangeDonation}
              working={pendingEvent === 'changeDonation'}
              disabled={!!pendingEvent && pendingEvent !== 'changeDonation'}
            />
          </div>
        )}
        {state === 'active' && (requestStatus === 'cancelled' || requestStatus === 'unfindabled') && (
          <ConfirmDialog
            labels={labels.reactivateConfirmDialog}
            onConfirm={onReactivate}
            working={pendingEvent === 'reactivateRequest'}
            disabled={!!pendingEvent && pendingEvent !== 'reactivateRequest'}
          />
        )}
      </Card>
    )
  }
)
